import { useState, type FormEvent } from 'react'
import { AlertTriangle, Check, Copy, ExternalLink, ShieldCheck } from 'lucide-react'
import { confirmSmbGoogle, type SmbGoogleConsent } from '../lib/api'

// BRIEF-B6b: the Google Workspace allow step. A Google business grants
// access in its own Google Admin console (domain-wide delegation), not in a
// pop-up, so this card walks the admin through it with the values the consent
// route returned (client id, scopes, console link: never hard-coded here),
// then asks who the administrator is and posts that to /google/confirm.
//
// - Failure: the route's own sentence is shown as written, the form stays,
//   and the button reads "Try again".
// - Success: the same next step as the Microsoft path, the consent-complete
//   landing with the session, where the owner chooses seats.
// - The email field is prefilled with the address given at sign-up when this
//   browser still holds it (SMB_CONTACT_EMAIL_KEY, written by /smb). The
//   status route does not echo it, so another browser starts blank.

export const SMB_CONTACT_EMAIL_KEY = 'dw_smb_contact_email'

export function googleNextStep(session: string): string {
  return `/smb/consent-complete?session=${encodeURIComponent(session)}&platform=google_workspace`
}

function storedContactEmail(): string {
  try {
    return localStorage.getItem(SMB_CONTACT_EMAIL_KEY) ?? ''
  } catch {
    return ''
  }
}

function forgetContactEmail() {
  try {
    localStorage.removeItem(SMB_CONTACT_EMAIL_KEY)
  } catch {
    // Nothing to forget.
  }
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setState('copied')
      window.setTimeout(() => setState('idle'), 2000)
    } catch {
      setState('failed')
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      className="tap-min shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-brass/60 text-sm font-medium text-navy hover:bg-brass/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
    >
      {state === 'copied' ? (
        <Check className="w-4 h-4 text-brass" aria-hidden="true" />
      ) : (
        <Copy className="w-4 h-4 text-brass" aria-hidden="true" />
      )}
      <span aria-live="polite">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select and copy' : 'Copy'}
      </span>
    </button>
  )
}

export function SmbGoogleAllowStep({
  session,
  grant,
  onConfirmed = () => window.location.assign(googleNextStep(session)),
}: {
  session: string
  grant: SmbGoogleConsent
  onConfirmed?: () => void
}) {
  const [adminEmail, setAdminEmail] = useState(storedContactEmail)
  const [sending, setSending] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  // Google's form takes the scopes comma separated, in one field.
  const scopesValue = grant.scopes.join(',')

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const email = adminEmail.trim()
    if (!email || sending) return
    setSending(true)
    const result = await confirmSmbGoogle(session, email)
    setSending(false)
    if (result.ok) {
      setRefusal(null)
      forgetContactEmail()
      onConfirmed()
      return
    }
    setRefusal(result.message)
  }

  const boxClass =
    'min-w-0 flex-1 rounded-lg bg-cream border border-gray-200 px-3 py-2 font-mono text-xs text-navy break-all'

  return (
    <section
      aria-label="organisation access"
      className="mb-6 bg-white rounded-xl border border-brass/40 p-4 min-w-0"
    >
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-brass mt-0.5 shrink-0" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-navy">
          In your Google Admin console, give DigitalWatch read-only access
        </h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Nothing is connected until you have done this. DigitalWatch gets
        read-only access and can never change anything in your Workspace.
      </p>

      <ol className="space-y-4 text-sm text-navy list-decimal pl-5">
        <li>
          <a
            href={grant.admin_console_url}
            target="_blank"
            rel="noopener noreferrer"
            className="tap-min inline-flex items-center gap-1.5 font-medium text-brass hover:text-brass-dark underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-brass rounded"
          >
            Open the Google Admin console
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
          <span className="block text-gray-500">
            It opens the page for domain-wide delegation. If it does not, go to
            Security, Access and data control, API controls, Manage Domain Wide
            Delegation, signed in as a super admin.
          </span>
        </li>
        <li>Choose Add new.</li>
        <li>
          <span className="block mb-1.5">Paste this client ID.</span>
          <div className="flex items-start gap-2 min-w-0">
            <code className={boxClass} data-testid="google-client-id">
              {grant.client_id}
            </code>
            <CopyButton value={grant.client_id} label="Copy the client ID" />
          </div>
        </li>
        <li>
          <span className="block mb-1.5">
            Paste these scopes into OAuth scopes, then choose Authorize.
          </span>
          <div className="flex items-start gap-2 min-w-0">
            <ul
              className={`${boxClass} max-h-40 overflow-y-auto space-y-1 list-none`}
              aria-label="scopes"
            >
              {grant.scopes.map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
            <CopyButton value={scopesValue} label="Copy the scopes" />
          </div>
        </li>
      </ol>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3">
        <div>
          <label htmlFor="smb-google-admin-email" className="block text-sm font-medium text-navy">
            Your Google Workspace administrator email
          </label>
          <input
            id="smb-google-admin-email"
            type="email"
            required
            autoComplete="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className="w-full mt-1 px-3 py-2.5 rounded-lg border border-gray-300 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass focus:border-brass"
          />
          <p className="text-xs text-gray-500 mt-1">
            The person signing up must be an administrator of this Google
            Workspace.
          </p>
        </div>

        {refusal && (
          <div className="rounded-lg bg-danger-light p-3 flex items-start gap-2" role="alert">
            <AlertTriangle className="w-4 h-4 text-danger mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-sm text-navy">{refusal}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={sending}
          className="w-full tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2 disabled:opacity-60"
        >
          {sending ? 'Checking with Google…' : refusal ? 'Try again' : 'I’ve done this'}
        </button>
      </form>
    </section>
  )
}
